// Wave 56 · Soundbox — app-wide live event listener.
// 1. Subscribes to ATC broadcast SSE stream (instant real-time for ATC events).
// 2. ALSO polls /api/soundbox/alerts every 8s for ALL other system events
//    (wallet transfers, marketplace orders, registrations, withdrawals, plans, auctions).
//
// Per-category mute is respected via isCategoryMuted() in announce().
import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { announce as sbAnnounce, isCategoryMuted } from '../utils/soundbox';
import api from '../utils/api';

// Map SSE event name -> soundbox template key + how to build its data payload.
const SSE_EVENT_HANDLERS = {
  demand_created:   (d) => [d.deal_type === 'B2B_BULK' ? 'new_b2b_demand' : 'new_b2c_demand', { title: d.title, amount: d.budget, qty: d.quantity }],
  bid_placed:       (d) => ['new_bid_received',   { amount: d.offered_price, kind: 'demand' }],
  deal_locked:      (d) => ['deal_locked',         { amount: d.amount }],
  new_order:        (d) => ['new_order',            { amount: d.amount }],
  payment_received: (d) => ['payment_received',    { amount: d.amount }],
  escrow_confirmed: (d) => ['escrow_confirmed',    { amount: d.amount }],
};

// Tracks IDs already announced so we don't repeat across polls
const _seen = new Set();

export default function useSoundboxEvents() {
  const { user } = useAuth();
  const esRef      = useRef(null);
  const lastTsRef  = useRef(0); // unix timestamp of last successful poll

  // ── 1. ATC SSE stream ──────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    if (!backendUrl) return;

    const es = new EventSource(`${backendUrl}/api/atc/broadcast/stream`);
    esRef.current = es;

    Object.entries(SSE_EVENT_HANDLERS).forEach(([evtName, buildFn]) => {
      es.addEventListener(evtName, (e) => {
        try {
          const d = JSON.parse(e.data);
          const [templateKey, data] = buildFn(d);
          sbAnnounce(templateKey, data);
        } catch (_) {}
      });
    });
    es.onerror = () => {};

    return () => { es.close(); esRef.current = null; };
  }, [user]);

  // ── 2. Polling /api/soundbox/alerts every 8s ──────────────────
  useEffect(() => {
    if (!user) return;

    const poll = async () => {
      try {
        const since = lastTsRef.current > 0 ? lastTsRef.current : 0;
        const res = await api.get(`/soundbox/alerts?since=${since}`);
        const { alerts = [], server_time } = res.data;

        for (const alert of alerts) {
          const key = alert.id || (alert.category + '_' + alert.ts);
          if (_seen.has(key)) continue;
          if (isCategoryMuted(alert.category)) continue;
          _seen.add(key);
          // Small stagger so multiple alerts don't pile up
          sbAnnounce(alert.category, alert.data || {});
        }

        if (server_time) lastTsRef.current = server_time - 2; // 2s buffer
        // Keep seen set bounded
        if (_seen.size > 2000) {
          const arr = [..._seen];
          arr.slice(0, 1000).forEach((k) => _seen.delete(k));
        }
      } catch (_) {}
    };

    // Initial poll (slightly delayed so auth is stable)
    const initTimer = setTimeout(poll, 3000);
    // Then every 8 seconds
    const interval  = setInterval(poll, 8000);

    return () => { clearTimeout(initTimer); clearInterval(interval); };
  }, [user]);
}
